import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useB2BUser, type AgentClientRecord } from './B2BUserContext';
import { useB2BTerms } from './B2BTermsContext';
import {
  Loader as Loader2, CircleAlert as AlertCircle, RotateCcw, Search,
  Package, CircleCheck as CheckCircle2, ArrowRight, Info, X,
  Store, Layers, Truck, Plus, Minus, ShoppingCart,
  ChevronLeft, FileText, Trash2, MessageSquare, Users,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CatalogProduct {
  id:            string;
  company_id:    string;
  sku:           string;
  name:          string;
  price:         number;
  image_url:     string | null;
  description:   string | null;
  group_name:    string | null;
  supplier_name: string | null;
  available_qty: number;
}

interface CartItem {
  product: CatalogProduct;
  qty:     number;
  reason:  string;
}

interface BulkReturnResult {
  rma_ids:            string[];
  standard_rma_id:    string | null;
  consignment_rma_id: string | null;
  total_items:        number;
  total_credit:       number;
  lines_count:        number;
  instant:            boolean;
}

type PageScreen = 'selectClient' | 'browse' | 'review' | 'success';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtPrice(n: number) {
  return `₪${Number(n).toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortId(id: string) {
  return id.slice(0, 8).toUpperCase();
}

// ─── Client Selector ─────────────────────────────────────────────────────────

function ClientSelector({
  clients, onSelect, agentName,
}: {
  clients:   AgentClientRecord[];
  onSelect:  (c: AgentClientRecord) => void;
  agentName: string | null;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.email ?? '').toLowerCase().includes(q) ||
      (c.phone ?? '').includes(q),
    );
  }, [clients, search]);

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-5" dir="rtl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center shrink-0">
          <RotateCcw className="w-5 h-5 text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">בקשת החזרה</h1>
          {agentName && (
            <p className="text-xs text-gray-500">
              <span className="font-medium text-orange-600">סוכן: {agentName}</span>
            </p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">בחר לקוח כדי להתחיל תעודת החזרה</p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="חפש לפי שם, טלפון או אימייל…"
          className="w-full pr-9 pl-4 py-2.5 rounded-xl border border-gray-200 bg-white
                     text-sm focus:outline-none focus:ring-2 focus:ring-orange-300
                     placeholder:text-gray-400"
        />
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center py-12 gap-3 text-gray-400">
          <Users className="w-12 h-12 text-gray-200" />
          <p className="text-sm font-medium">
            {clients.length === 0 ? 'אין לקוחות משויכים' : 'לא נמצאו תוצאות'}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 px-1">{filtered.length} לקוחות</p>
          {filtered.map(client => (
            <button
              key={client.id}
              onClick={() => onSelect(client)}
              className="w-full flex items-center gap-3 p-4 bg-white rounded-2xl
                         border border-gray-100 shadow-sm hover:shadow-md
                         hover:border-orange-200 active:scale-[.99]
                         transition-all text-right"
            >
              <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center
                              justify-center shrink-0">
                <Store className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex-1 min-w-0 space-y-0.5">
                <p className="font-semibold text-gray-900 truncate text-sm">{client.name}</p>
                {client.phone && (
                  <p className="text-xs text-gray-500" dir="ltr">{client.phone}</p>
                )}
              </div>
              <ChevronLeft className="w-4 h-4 text-gray-300 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Product Detail Popup ────────────────────────────────────────────────────

function ProductDetailPopup({
  product, onClose, onAdd, cartQty,
}: {
  product:  CatalogProduct;
  onClose:  () => void;
  onAdd:    (p: CatalogProduct) => void;
  cartQty:  number;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end
                 sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      dir="rtl"
    >
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl
                   shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-56 object-cover bg-gray-100"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
            <Package className="w-16 h-16 text-gray-200" />
          </div>
        )}

        <div className="px-5 py-4 space-y-3">
          <div>
            <h3 className="text-base font-bold text-gray-900">{product.name}</h3>
            <p className="text-xs text-gray-400 font-mono mt-0.5" dir="ltr">{product.sku}</p>
          </div>

          {product.description && (
            <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
          )}

          <div className="flex flex-wrap gap-2 text-xs">
            {product.group_name && (
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                {product.group_name}
              </span>
            )}
            <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
              מחיר: {fmtPrice(product.price)}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
              מלאי: {product.available_qty}
            </span>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-2xl border border-gray-200
                         text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              סגור
            </button>
            <button
              type="button"
              onClick={() => { onAdd(product); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl
                         bg-gray-900 hover:bg-gray-800 active:scale-[.99]
                         text-white text-sm font-semibold transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              {cartQty > 0 ? `הוסף עוד (${cartQty} בעגלה)` : 'הוסף להחזרה'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Cart Bar (sticky bottom) ────────────────────────────────────────────────

function CartBar({ cart, onReview }: { cart: CartItem[]; onReview: () => void }) {
  const totalQty = cart.reduce((sum, item) => sum + item.qty, 0);
  if (cart.length === 0) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 px-4 pb-4 pt-2
                    bg-gradient-to-t from-white via-white to-transparent pointer-events-none">
      <div className="pointer-events-auto max-w-2xl mx-auto" dir="rtl">
        <button
          type="button"
          onClick={onReview}
          className="w-full flex items-center justify-between gap-3 px-5 py-3.5
                     bg-gray-900 hover:bg-gray-800 active:scale-[.99]
                     text-white rounded-2xl shadow-xl transition-all"
        >
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <ShoppingCart className="w-5 h-5" />
              <span className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-orange-500
                               text-[10px] font-bold text-white rounded-full
                               flex items-center justify-center">
                {cart.length}
              </span>
            </div>
            <span className="text-sm font-semibold">
              {cart.length} מוצרים · {totalQty} יח׳
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            סקירה ושליחה
            <ArrowRight className="w-4 h-4 rotate-180" />
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── Review Modal ────────────────────────────────────────────────────────────

function ReviewModal({
  cart, isAgent, clientName,
  onUpdateQty, onUpdateReason, onRemove, onSubmit, onClose,
  submitting, error,
}: {
  cart:           CartItem[];
  isAgent:        boolean;
  clientName:     string;
  onUpdateQty:    (id: string, qty: number) => void;
  onUpdateReason: (id: string, reason: string) => void;
  onRemove:       (id: string) => void;
  onSubmit:       (instant: boolean) => void;
  onClose:        () => void;
  submitting:     boolean;
  error:          string | null;
}) {
  const [instantReturn, setInstantReturn] = useState(false);
  const totalQty = cart.reduce((sum, i) => sum + i.qty, 0);

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end
                 sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      dir="rtl"
    >
      <div
        className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl
                   shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-500" />
              תעודת החזרה
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {clientName} · {cart.length} מוצרים · {totalQty} יח׳
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cart lines */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {cart.map(item => (
            <div key={item.product.id}
              className="rounded-2xl border border-orange-100 bg-orange-50/30 p-3 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-1">
                    {item.product.name}
                  </p>
                  <p className="text-[10px] text-gray-400 font-mono" dir="ltr">
                    {item.product.sku}
                  </p>
                </div>
                <button type="button" onClick={() => onRemove(item.product.id)}
                  className="p-1.5 rounded-lg text-gray-300 hover:text-red-500
                             hover:bg-red-50 transition-colors shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Qty adjuster */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium w-14">כמות:</span>
                <div className="flex items-center gap-0 border border-gray-200 rounded-xl overflow-hidden bg-white">
                  <button type="button"
                    onClick={() => onUpdateQty(item.product.id, Math.max(1, item.qty - 1))}
                    className="w-8 h-8 flex items-center justify-center text-gray-500
                               hover:bg-gray-100 transition-colors">
                    <Minus className="w-3 h-3" />
                  </button>
                  <input
                    type="number" min={1} value={item.qty}
                    onChange={e => {
                      const n = parseInt(e.target.value, 10);
                      if (!isNaN(n) && n >= 1) onUpdateQty(item.product.id, n);
                    }}
                    className="w-12 h-8 text-center text-sm font-bold tabular-nums
                               border-x border-gray-200 bg-white focus:outline-none"
                  />
                  <button type="button"
                    onClick={() => onUpdateQty(item.product.id, item.qty + 1)}
                    className="w-8 h-8 flex items-center justify-center text-gray-500
                               hover:bg-gray-100 transition-colors">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Reason */}
              <div className="flex items-start gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-gray-300 mt-2 shrink-0" />
                <input
                  type="text"
                  value={item.reason}
                  onChange={e => onUpdateReason(item.product.id, e.target.value)}
                  placeholder="סיבת החזרה (אופציונלי)"
                  className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200
                             bg-white focus:outline-none focus:ring-1 focus:ring-orange-300
                             placeholder:text-gray-300"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-4 space-y-3 shrink-0">
          {isAgent && (
            <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl
                              border border-gray-100 bg-gray-50 cursor-pointer
                              hover:border-orange-200 transition-colors">
              <input
                type="checkbox"
                checked={instantReturn}
                onChange={e => setInstantReturn(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-orange-500
                           focus:ring-orange-400 accent-orange-500"
              />
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-orange-500" />
                  החזרה מיידית לוואן
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  המלאי יעודכן מיד ותופק תעודה אוטומטית
                </p>
              </div>
            </label>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border
                            border-red-200 rounded-xl text-xs text-red-700">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          <button
            type="button"
            onClick={() => onSubmit(instantReturn)}
            disabled={submitting || cart.length === 0}
            className="w-full flex items-center justify-center gap-2
                       py-3.5 rounded-2xl bg-gray-900 hover:bg-gray-800
                       disabled:opacity-50 disabled:cursor-not-allowed
                       text-white text-sm font-semibold transition-colors"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> שולח…</>
            ) : (
              <><RotateCcw className="w-4 h-4" /> {instantReturn ? 'אשר החזרה מיידית' : 'שלח בקשת החזרה'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Success Screen ──────────────────────────────────────────────────────────

function SuccessScreen({
  result, cart, clientName, onDone,
}: {
  result:     BulkReturnResult;
  cart:       CartItem[];
  clientName: string;
  onDone:     () => void;
}) {
  return (
    <div className="max-w-xl mx-auto px-4 py-8" dir="rtl">
      <div className="bg-white rounded-3xl shadow-sm border border-green-100 overflow-hidden">
        <div className={`px-6 py-6 text-center space-y-3
                        ${result.instant
                          ? 'bg-gradient-to-br from-emerald-50 to-green-50'
                          : 'bg-gradient-to-br from-green-50 to-emerald-50'}`}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto
                          ${result.instant ? 'bg-emerald-100' : 'bg-green-100'}`}>
            {result.instant
              ? <Truck className="w-8 h-8 text-emerald-600" />
              : <CheckCircle2 className="w-8 h-8 text-green-600" />}
          </div>
          <h1 className="text-xl font-bold text-gray-900">
            {result.instant ? 'ההחזרה אושרה ובוצעה' : 'תעודת ההחזרה נשלחה'}
          </h1>
          <p className="text-sm text-gray-500">
            {clientName} · {result.total_items} יח׳ ב-{result.lines_count} מוצרים
          </p>
          {result.instant ? (
            <p className="text-xs text-emerald-600 font-medium">
              מסמכי ההחזרה הופקו והמלאי עודכן אוטומטית.
            </p>
          ) : (
            <p className="text-xs text-gray-400">
              הבקשה נשלחה לאישור מנהל. תקבל עדכון עם אישור ההחזרה.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 px-5 py-4">
          <div className="rounded-2xl bg-gray-50 px-4 py-3 space-y-0.5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">מזהה בקשה</p>
            {result.rma_ids.map(id => (
              <p key={id} className="text-xs font-mono text-gray-700">#{shortId(id)}</p>
            ))}
          </div>
          <div className="rounded-2xl bg-orange-50 border border-orange-100 px-4 py-3 space-y-0.5">
            <p className="text-[10px] text-orange-500 uppercase tracking-wide font-semibold
                          flex items-center gap-1">
              <Layers className="w-3 h-3" />
              סה״כ זיכוי
            </p>
            <p className="text-sm font-bold text-orange-700 tabular-nums">
              {fmtPrice(result.total_credit)}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-100 px-5 py-3 space-y-2">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">פירוט מוצרים</p>
          {cart.map(item => (
            <div key={item.product.id}
              className="flex items-center gap-2.5 py-2 border-b border-gray-50 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 line-clamp-1">{item.product.name}</p>
                <p className="text-[10px] text-gray-400 font-mono" dir="ltr">{item.product.sku}</p>
                {item.reason && (
                  <span className="text-[10px] text-gray-400 truncate">{item.reason}</span>
                )}
              </div>
              <span className="text-sm font-bold text-gray-700 tabular-nums shrink-0">
                {item.qty} יח׳
              </span>
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-gray-100">
          <button onClick={onDone}
            className="w-full flex items-center justify-center gap-2
                       py-3 rounded-2xl bg-gray-900 hover:bg-gray-800
                       text-white text-sm font-semibold transition-colors">
            <ArrowRight className="w-4 h-4" />
            חזור לרשימה
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function B2BLifoReturnPage() {
  const location    = useLocation();
  const { session } = useB2BUser();
  const isAgent     = session?.role === 'agent';

  // Returns-enabled check
  const [returnsEnabled, setReturnsEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    const cid = session?.activeCompanyId;
    if (!cid) return;
    supabase
      .from('companies')
      .select('b2b_returns_enabled')
      .eq('id', cid)
      .single()
      .then(({ data }) => {
        setReturnsEnabled(data ? Boolean((data as Record<string, unknown>).b2b_returns_enabled) : true);
      });
  }, [session?.activeCompanyId]);

  // Screen + selection state
  const [screen, setScreen]               = useState<PageScreen>('selectClient');
  const [selectedClient, setSelectedClient] = useState<AgentClientRecord | null>(null);

  // Products (loaded via get_b2b_catalog -- same as the ordering screen)
  const [products, setProducts]     = useState<CatalogProduct[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');

  // Detail popup
  const [detailProduct, setDetailProduct] = useState<CatalogProduct | null>(null);

  // Cart
  const [cart, setCart] = useState<CartItem[]>([]);

  // Submit
  const [submitting, setSubmitting]      = useState(false);
  const [submitError, setSubmitError]    = useState<string | null>(null);
  const [result, setResult]              = useState<BulkReturnResult | null>(null);
  const [submittedCart, setSubmittedCart] = useState<CartItem[]>([]);

  // Resolve company ID
  const companyId = useMemo(() => {
    const navState = location.state as { companyId?: string } | null;
    return navState?.companyId
      ?? session?.activeCompanyId
      ?? session?.allowedCompanyIds?.[0]
      ?? null;
  }, [location.state, session]);

  // Resolve available clients (agents only)
  const agentClients = useMemo(() => {
    if (!isAgent || !session) return [];
    const records = session.agentClientRecords ?? [];
    const activeComp = session.activeCompanyId;
    if (activeComp && (session.availableCompanies?.length ?? 0) > 1) {
      return records.filter(c => c.company_id === activeComp);
    }
    return records;
  }, [isAgent, session]);

  // Auto-select client if already picked in another page
  useEffect(() => {
    if (session?.activeClientRecord && !selectedClient) {
      setSelectedClient(session.activeClientRecord as AgentClientRecord);
      setScreen('browse');
    }
  }, [session?.activeClientRecord, selectedClient]);

  // ── Load products that have positive stock in the client's warehouse ────
  const loadProducts = useCallback(async () => {
    if (!companyId || !selectedClient) return;

    const warehouseId = selectedClient.consignment_warehouse_id;
    if (!warehouseId) {
      setProducts([]);
      setError('ללקוח זה לא מוגדר מחסן – לא ניתן להציג מוצרים להחזרה');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const targetIds = [companyId];

      // Fetch warehouse balances AND catalog in parallel
      const [balancesRes, catalogRes] = await Promise.all([
        supabase.rpc('b2b_agent_inventory_balances', {
          p_company_id:   companyId,
          p_warehouse_id: warehouseId,
        }),
        supabase
          .rpc('get_b2b_catalog', { p_company_ids: targetIds })
          .limit(10000),
      ]);

      if (balancesRes.error) throw new Error(balancesRes.error.message);
      if (catalogRes.error) throw new Error(catalogRes.error.message);

      // Build a map: product_id → qty in the client's warehouse (positive only)
      const warehouseQtyMap = new Map<string, number>();
      for (const row of (balancesRes.data ?? []) as any[]) {
        const qty = Number(row.balance ?? row.qty ?? 0);
        if (qty > 0) {
          warehouseQtyMap.set(row.product_id, qty);
        }
      }

      // Apply pricelist overrides
      const priceOverrideMap = new Map<string, number>();
      const pricelistByCompany = session?.pricelistByCompany ?? {};
      const companiesWithPricelist = targetIds
        .map(cid => ({ companyId: cid, pricelistId: pricelistByCompany[cid] }))
        .filter((x): x is { companyId: string; pricelistId: string } => !!x.pricelistId);

      if (companiesWithPricelist.length > 0) {
        const byPricelist = new Map<string, string[]>();
        for (const { companyId: cid, pricelistId } of companiesWithPricelist) {
          const arr = byPricelist.get(pricelistId) ?? [];
          arr.push(cid);
          byPricelist.set(pricelistId, arr);
        }
        const priceResults = await Promise.all(
          Array.from(byPricelist.entries()).map(([pricelistId, cids]) =>
            supabase.rpc('get_b2b_pricelist_prices', {
              p_company_ids:  cids,
              p_pricelist_id: pricelistId,
            }),
          ),
        );
        for (const r of priceResults) {
          if (r.data) {
            for (const row of r.data as any[]) {
              if (row.price_after_discount != null) {
                priceOverrideMap.set(row.product_id, Number(row.price_after_discount));
              }
            }
          }
        }
      }

      // Keep only catalog products that exist in the warehouse with qty > 0
      setProducts(
        (catalogRes.data ?? [])
          .filter((r: any) => warehouseQtyMap.has(r.id))
          .map((r: any) => ({
            id:            r.id,
            company_id:    r.company_id,
            sku:           r.sku ?? '',
            name:          r.name ?? 'מוצר',
            price:         priceOverrideMap.get(r.id) ?? Number(r.price ?? 0),
            image_url:     r.image_url ?? null,
            description:   r.description ?? null,
            group_name:    r.group_name ?? null,
            supplier_name: r.supplier_name ?? null,
            available_qty: warehouseQtyMap.get(r.id) ?? 0,
          })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת מוצרים');
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedClient, session?.pricelistByCompany]);

  useEffect(() => {
    if (screen === 'browse' && selectedClient) loadProducts();
  }, [screen, selectedClient, loadProducts]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.sku.toLowerCase().includes(q),
    );
  }, [products, search]);

  // Cart helpers
  const cartQtyMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const item of cart) m.set(item.product.id, item.qty);
    return m;
  }, [cart]);

  const addToCart = useCallback((product: CatalogProduct) => {
    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) {
        return prev.map(i => i.product.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { product, qty: 1, reason: '' }];
    });
  }, []);

  const decreaseFromCart = useCallback((productId: string) => {
    setCart(prev => {
      const existing = prev.find(i => i.product.id === productId);
      if (!existing) return prev;
      if (existing.qty <= 1) return prev.filter(i => i.product.id !== productId);
      return prev.map(i => i.product.id === productId ? { ...i, qty: i.qty - 1 } : i);
    });
  }, []);

  const updateCartQty = useCallback((productId: string, qty: number) => {
    setCart(prev => prev.map(i => i.product.id === productId ? { ...i, qty } : i));
  }, []);

  const updateCartReason = useCallback((productId: string, reason: string) => {
    setCart(prev => prev.map(i => i.product.id === productId ? { ...i, reason } : i));
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prev => prev.filter(i => i.product.id !== productId));
  }, []);

  // Submit
  const { remindIfNeeded: remindTerms } = useB2BTerms();
  const handleSubmit = useCallback(async (instant: boolean) => {
    if (!companyId || !selectedClient || cart.length === 0) return;
    await remindTerms();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const lines = cart.map(item => ({
        product_id: item.product.id,
        qty:        item.qty,
        reason:     item.reason.trim() || null,
        order_type: 'consignment',
      }));

      const { data, error: rpcErr } = await supabase.rpc('submit_bulk_return', {
        p_company_id: companyId,
        p_client_id:  selectedClient.id,
        p_lines:      lines,
        p_instant:    instant,
      });

      if (rpcErr) throw new Error(rpcErr.message);
      if (!data || !data.rma_ids) throw new Error('שגיאה בתוצאת ההחזרה');

      setSubmittedCart([...cart]);
      setResult({
        rma_ids:            data.rma_ids ?? [],
        standard_rma_id:    data.standard_rma_id ?? null,
        consignment_rma_id: data.consignment_rma_id ?? null,
        total_items:        Number(data.total_items ?? 0),
        total_credit:       Number(data.total_credit ?? 0),
        lines_count:        Number(data.lines_count ?? 0),
        instant:            Boolean(data.instant),
      });
      setScreen('success');
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'שגיאה בשליחת ההחזרה');
    } finally {
      setSubmitting(false);
    }
  }, [companyId, selectedClient, cart, remindTerms]);

  const handleDone = useCallback(() => {
    setResult(null);
    setSubmittedCart([]);
    setCart([]);
    setScreen('browse');
    loadProducts();
  }, [loadProducts]);

  const handleSelectClient = useCallback((client: AgentClientRecord) => {
    setSelectedClient(client);
    setCart([]);
    setProducts([]);
    setScreen('browse');
  }, []);

  const handleChangeClient = useCallback(() => {
    setSelectedClient(null);
    setCart([]);
    setProducts([]);
    setScreen('selectClient');
  }, []);

  // ── Redirects ──────────────────────────────────────────────────────────
  if (returnsEnabled === false) return <Navigate to="/b2b/orders" replace />;

  // ── Success ────────────────────────────────────────────────────────────
  if (screen === 'success' && result) {
    return (
      <SuccessScreen
        result={result}
        cart={submittedCart}
        clientName={selectedClient?.name ?? ''}
        onDone={handleDone}
      />
    );
  }

  // ── Client selection ───────────────────────────────────────────────────
  if (screen === 'selectClient' && isAgent) {
    return (
      <ClientSelector
        clients={agentClients}
        onSelect={handleSelectClient}
        agentName={session?.agentRecord?.name ?? null}
      />
    );
  }

  // ── Browse products ────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-28" dir="rtl">
      {/* Title + client */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <RotateCcw className="w-5 h-5 text-orange-500" />
          <div>
            <h1 className="text-lg font-bold text-gray-900">תעודת החזרה</h1>
            {selectedClient && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Store className="w-3 h-3 text-orange-400" />
                {selectedClient.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && !error && products.length > 0 && (
            <span className="text-xs font-semibold text-gray-500 bg-gray-100
                             px-2.5 py-1 rounded-full">
              {products.length} מוצרים
            </span>
          )}
          {isAgent && selectedClient && (
            <button type="button" onClick={handleChangeClient}
              className="text-xs text-orange-600 hover:text-orange-700 font-medium
                         px-2.5 py-1 rounded-lg hover:bg-orange-50 transition-colors">
              החלף לקוח
            </button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="flex items-start gap-2 px-3.5 py-2.5 bg-blue-50
                      border border-blue-100 rounded-xl text-[11px] text-blue-700">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>
          בחר מוצרים מהרשימה והוסף אותם לתעודת ההחזרה.
          לחץ על שורה לצפייה בפרטי המוצר.
        </p>
      </div>

      {/* Search */}
      {!loading && !error && products.length > 0 && (
        <div className="relative">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="חיפוש לפי שם / מק״ט…"
            className="w-full h-11 pr-10 pl-3 rounded-2xl border border-gray-200
                       bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-300
                       placeholder:text-gray-300"
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
          <p className="text-sm">טוען מוצרים…</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center gap-4 py-16">
          <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border
                          border-red-200 rounded-2xl text-sm text-red-700 w-full max-w-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">שגיאה בטעינת מוצרים</p>
              <p className="text-xs mt-0.5 text-red-500">{error}</p>
            </div>
          </div>
          <button onClick={loadProducts}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium">
            נסה שנית
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && products.length === 0 && selectedClient && (
        <div className="flex flex-col items-center justify-center py-20 gap-5">
          <div className="flex items-center justify-center w-20 h-20 rounded-3xl bg-gray-100">
            <Package className="w-10 h-10 text-gray-300" />
          </div>
          <div className="text-center space-y-1.5">
            <h2 className="text-lg font-bold text-gray-800">אין מוצרים במלאי</h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              לא נמצאו מוצרים עם מלאי חיובי במחסן של הלקוח.
            </p>
          </div>
        </div>
      )}

      {/* No search results */}
      {!loading && !error && products.length > 0 && filteredProducts.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-400">
          לא נמצאו מוצרים התואמים לחיפוש.
        </div>
      )}

      {/* Product list -- simple text rows, no images */}
      {!loading && !error && filteredProducts.length > 0 && (
        <div className="space-y-1">
          {filteredProducts.map(p => {
            const inCart = (cartQtyMap.get(p.id) ?? 0) > 0;
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                           cursor-pointer select-none
                           ${inCart
                             ? 'bg-orange-50 border border-orange-200'
                             : 'bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm'}`}
              >
                {/* Tappable area: product info -> opens detail */}
                <div
                  className="flex-1 min-w-0"
                  onClick={() => setDetailProduct(p)}
                >
                  <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-400 font-mono" dir="ltr">{p.sku}</span>
                    {p.group_name && (
                      <span className="text-[10px] text-gray-400">· {p.group_name}</span>
                    )}
                  </div>
                </div>

                {/* Add / quantity controls */}
                {!inCart ? (
                  <button
                    type="button"
                    onClick={() => addToCart(p)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg
                               bg-gray-900 hover:bg-gray-800 active:scale-95
                               text-white text-xs font-semibold transition-all shrink-0"
                  >
                    <Plus className="w-3 h-3" />
                    הוסף
                  </button>
                ) : (
                  <div className="flex items-center gap-0 border border-orange-200
                                  rounded-lg overflow-hidden bg-white shrink-0">
                    <button type="button" onClick={() => decreaseFromCart(p.id)}
                      className="w-7 h-7 flex items-center justify-center text-gray-500
                                 hover:bg-gray-100 transition-colors">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-7 h-7 flex items-center justify-center text-xs
                                   font-bold tabular-nums bg-orange-50 text-orange-700">
                      {cartQtyMap.get(p.id)}
                    </span>
                    <button type="button" onClick={() => addToCart(p)}
                      className="w-7 h-7 flex items-center justify-center text-gray-500
                                 hover:bg-gray-100 transition-colors">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cart bar */}
      <CartBar cart={cart} onReview={() => setScreen('review')} />

      {/* Review modal */}
      {screen === 'review' && (
        <ReviewModal
          cart={cart}
          isAgent={isAgent}
          clientName={selectedClient?.name ?? ''}
          onUpdateQty={updateCartQty}
          onUpdateReason={updateCartReason}
          onRemove={removeFromCart}
          onSubmit={handleSubmit}
          onClose={() => setScreen('browse')}
          submitting={submitting}
          error={submitError}
        />
      )}

      {/* Product detail popup */}
      {detailProduct && (
        <ProductDetailPopup
          product={detailProduct}
          cartQty={cartQtyMap.get(detailProduct.id) ?? 0}
          onClose={() => setDetailProduct(null)}
          onAdd={addToCart}
        />
      )}
    </div>
  );
}
